import {Hide, VStack} from 'platform/foundation';
import {useFormatNumber, useFormatPercentage} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {isNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../../../i18n/i18n';
import {MarketType} from '../../../../../types/MarketType';
import {PriceReportType} from '../../../../../types/PriceReportType';
import {getDifference} from '../../../../../utils/getDifference';
import {RealSellingPriceBanner} from './RealSellingPriceBanner';

const FEATURE_DIFFERENCE_THRESHOLD = 0.02;

interface RealSellingPriceCardProps extends TestIdProps {
  priceReport: PriceReportType;
  market?: MarketType;
}

export function RealSellingPriceBanners(props: RealSellingPriceCardProps) {
  const formatNumber = useFormatNumber();
  const formatPercentage = useFormatPercentage();

  const isSomeVehicleSold = (props.market?.sold?.similarVehicle ?? 0) > 0;

  const averageMileage = props.market?.sold?.averageMileage?.value ?? 0;
  const mileageDifference = props.priceReport.vehicle.mileage - averageMileage;

  const mileageBannerVariant = mileageDifference > 0 ? 'warning' : 'success';
  const mileageBannerHeading =
    mileageDifference > 0
      ? i18n.t('entity.priceMeter.labels.mileageBannerHigher', {
          mileage: formatNumber(Math.abs(mileageDifference)),
        })
      : i18n.t('entity.priceMeter.labels.mileageBannerLower', {
          mileage: formatNumber(Math.abs(mileageDifference)),
        });

  const featuresLevel = props.priceReport.featuresLevel ?? 0;
  const averageFeatureLevel = props.market?.sold?.averageFeatureLevel ?? 0;

  const featureDifference = getDifference(featuresLevel, averageFeatureLevel, 100);

  const featureBannerVariant = match<number | undefined, 'warning' | 'success' | undefined>(
    featureDifference
  )
    .with(Pattern.number.lt(0), () => 'warning')
    .with(Pattern.number.gt(0), () => 'success')
    .otherwise(() => undefined);
  const featureBannerHeading = match(featureDifference)
    .with(Pattern.number.lt(0), (value) =>
      i18n.t('entity.priceMeter.labels.featureBannerWorse', {
        percentage: formatPercentage(Math.abs(value), 'nearest'),
      })
    )
    .with(Pattern.number.gt(0), (value) =>
      i18n.t('entity.priceMeter.labels.featureBannerBetter', {
        percentage: formatPercentage(Math.abs(value), 'nearest'),
      })
    )
    .otherwise(() => undefined);
  const featureBannerMessage = i18n.t('entity.priceMeter.labels.featureBannerMessage');

  return (
    <VStack spacing={2}>
      <Hide
        when={
          Math.abs(mileageDifference) === 0 ||
          isNil(props.market?.sold?.averageMileage?.value) ||
          !isSomeVehicleSold
        }
      >
        <RealSellingPriceBanner
          variant={mileageBannerVariant}
          heading={mileageBannerHeading}
          data-testid={suffixTestId('mileageBanner', props)}
        />
      </Hide>
      <Hide
        when={
          Math.abs(featureDifference ?? 0) <= FEATURE_DIFFERENCE_THRESHOLD ||
          isNil(props.priceReport.featuresLevel) ||
          isNil(props.market?.sold?.averageFeatureLevel) ||
          !isSomeVehicleSold
        }
      >
        <RealSellingPriceBanner
          variant={featureBannerVariant}
          heading={featureBannerHeading}
          message={featureBannerMessage}
          data-testid={suffixTestId('featureBanner', props)}
        />
      </Hide>
    </VStack>
  );
}
